import React, { useState, useRef, useEffect } from 'react';
import './style.scss';
import paperClip from '../../../../../assets/images/paperclip.svg';
import Button from '../../../../Shared/components/controls/Button/index';
import { withTranslation } from 'react-i18next';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import Loader from '../../../../IBANGeneratorComponent/Loader/index';
import config from '../../../../../Config.js';
import dateIcon from '../../../../../assets/images/dateIcon.svg';
import { set } from 'lodash';
import { getCSRFToken, refreshCSRFTokenIfNeeded, clearCSRFToken } from '../../../../../utils/csrf';
import { decryptData, validateEncryptionKey } from '../../../../../utils/encryptionUtils';


const DocumentStepOne = ({
  setActiveStep,
  t,
  ExtensionLimitDetailsEndpoint,
  ExtraUserDetailsEndpoint,
  i18n,
  IsMockExtraUserDetailsEndpoint,
  GenerateOTPEndpoint,
  IsMockGenerateOTPEndpoint,
  extensionLimitDetails,
  setExtensionLimitDetails,
  extraUserData,
  setExtraUserData,
  generateOTPResponse,
  setGenerateOTPResponse,
  filesData,
  setFilesData, 
  filesizeErrorMessage,        
  setFilesizeErrorMessage  
}) => {
  const [extensionLimitDetailsError, setExtensionLimitDetailsError] = useState(null);
  const [extraUserDataError, setExtraUserDataError] = useState();
  const [generateOTPResponseError, setGenerateOTPResponseError] = useState();
  const [fileNames, setFileNames] = useState({});
  const [fileNamesError, setFileNamesError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [encryptionReady, setEncryptionReady] = useState(false);

  // Validate encryption configuration on component mount
  useEffect(() => {
    const isValid = validateEncryptionKey();
    setEncryptionReady(isValid);
    
    if (!isValid) {
     // console.error('Encryption configuration is invalid');
      setExtraUserDataError(t('EncryptionConfigError') || 'Application configuration error. Please contact support.');
    }
  }, [t]);

  useEffect(async () => {
    // Function to fetch extension limit form data
    const fetchExtensionLimitForm = async () => {
      try {
        // Retrieve RequestId from the URL's query parameters
        const urlParams = new URLSearchParams(window.location.search);
        const requestId = urlParams.get('RequestId');

        // Ensure requestId is present
        if (!requestId) {
          throw new Error('RequestId parameter is missing in the URL');
        }

        // Get the API host
        const apiHost = config.sitecoreApiHost || config.hostname || window.location.origin;

        // Ensure we have a valid CSRF token
        await refreshCSRFTokenIfNeeded(apiHost);
        const csrfToken = getCSRFToken();

        if (!csrfToken) {
          //console.error('CSRF token not available');
          setExtensionLimitDetailsError(
            t('SecurityErrorMsg') || 'Security validation failed. Please refresh the page.'
          );
          return;
        }

        const endpointUrl = `${config.hostname}${ExtensionLimitDetailsEndpoint}`;

        const response = await fetch(endpointUrl, {
          method: 'POST',
          credentials: 'include', // Important for cookies
          headers: {
            'Content-Type': 'application/json',
            __RequestVerificationToken: csrfToken,
          },
          body: JSON.stringify({
            RequestId: requestId,
          }),
        });

        // Handle CSRF token validation failure
        if (response.status === 403) {
         // console.warn('CSRF validation failed. Clearing token...');
          clearCSRFToken();
          setExtensionLimitDetailsError(
            t('SecurityErrorMsg') || 'Security validation failed. Please refresh the page.'
          );
          return;
        }

        if (response.status === 401) {
          setExtensionLimitDetailsError(
            t('AuthenticationErrorMsg') || 'Authentication required. Please refresh the page.'
          );
          return;
        }

        // Check if the response is ok
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

           const encryptedResponse = await response.json();

            // Check if response contains encrypted data
                       if (encryptedResponse.Data && encryptedResponse.Data.CipherText && encryptedResponse.Data.IV) {
                      //   console.log('Encrypted response received, decrypting...');
                         
                         try {
                           // Decrypt the data using key from config
                           const decryptedData = await decryptData(encryptedResponse.Data);
                        //   console.log('Data decrypted successfully'); 
                           setExtensionLimitDetails(decryptedData.Item2)
                          } catch (decryptError) {
                          // console.error('Decryption error:', decryptError);
                           throw new Error('Failed to decrypt response data. Please try again or contact support.');
                         }
                       } else { 
                          
                         window.location.href = '/en/personal-banking/credit-cards/Credit-Card-International-Limits';
 
                       }

        
      } catch (error) {
       // console.error('Error fetching extension limit form:', error);

        // Set user-friendly error messages
        if (error.message.includes('CSRF') || error.message.includes('Security')) {
          setExtensionLimitDetailsError(
            t('SecurityErrorMsg') || 'Security validation failed. Please refresh the page.'
          );
        } else if (error.message.includes('Authentication')) {
          setExtensionLimitDetailsError(
            t('AuthenticationErrorMsg') || 'Authentication required. Please refresh the page.'
          );
        } else {
          setExtensionLimitDetailsError(t('GenericErrorMsg') || 'An error occurred. Please try again.');
        }
      }
    };

    fetchExtensionLimitForm();
  }, []);


  const trips = extensionLimitDetails ? extensionLimitDetails?.Trips : [];
  
  const isAllFilesUploaded = () => {
    if (Object.keys(filesData).length === 0) {
      return false;  // No trips, so files are not uploaded
    }
    return Object.values(filesData).every((trip) =>
      ["Passport1stPage", "PassportStampedPage"].every((fileType) =>
        trip[fileType] instanceof File
      )
    );
  };

  useEffect(() => {
    const isValid = isAllFilesUploaded();
    setIsFormValid(isValid);
  }, [filesData]);

  // Helper function to convert a file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]); // Extract base64 data
      reader.onerror = (error) => reject(error);
    });
  };

  const validateFileName = (fileName) => {
    const pattern = /^[\w\*\$][\w\s\-\$]*(\(\d{1,}\)){0,1}$/;
    let regex = new RegExp(/\.[^/.]+$/);
    let NewfileName = fileName.replace(regex, '');
    return pattern.test(NewfileName);
  };

  const handleFileChange = async (tripID, fileType, event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        if (!validateFileName(file.name)) {
          setFileNamesError((prevErrors) => ({
            ...prevErrors,
            [tripID]: {
              ...prevErrors[tripID],
              [fileType]: t('FileNameErrorMessage'), // Use localization for error message
            },
          }));
          setFileNames((prev) => ({
            ...prev,
            [tripID]: {
              ...prev[tripID],
              [fileType]: '',
            },
          }));
          setFilesData((prev) => ({
            ...prev,
            [tripID]: {
              ...prev[tripID],
              [fileType]: null,
            },
          }));
          return;
        }
        // Clear error if validation passes
        setFileNamesError((prevErrors) => ({
          ...prevErrors,
          [tripID]: {
            ...prevErrors[tripID],
            [fileType]: '',
          },
        }));
        //   const base64 = await fileToBase64(file);
        setFileNames((prev) => ({
          ...prev,
          [tripID]: {
            ...prev[tripID],
            [fileType]: file.name,
          },
        }));

        // Update fileData state with base64, name, and type
        setFilesData((prev) => ({
          ...prev,
          [tripID]: {
            ...prev[tripID],
            [fileType]: file 
          },
        }));
      } catch (error) {
        console.log(error);
      }
    }
  };

  // Function to construct the final document structure
  // const constructDocumentsArray = (CustomerNo) => {
  //   return {
  //     Documents: Object.entries(filesData).map(([tripID, files]) => ({
  //       TripID: tripID,
  //       RequestID: extensionLimitDetails.Data.RequestID,
  //       CIF: CustomerNo,
  //       Passport1stPage: files.Passport1stPage || {},
  //       PassportStampedPage: files.PassportStampedPage || {},
  //     })),
  //   };
  // };

  const constructDocumentsFormData = (CustomerNo) => {
    const formData = new FormData();

    Object.entries(filesData).forEach(([tripID, files], index) => {
      formData.append(`Documents[${index}][TripID]`, tripID);
      formData.append(`Documents[${index}][RequestID]`, extensionLimitDetails.RequestID);
      formData.append(`Documents[${index}][CIF]`, CustomerNo);

      if (files.Passport1stPage) {
        formData.append(`Documents[${index}][Passport1stPage]`, files.Passport1stPage);
      }
      if (files.PassportStampedPage) {
        formData.append(`Documents[${index}][PassportStampedPage]`, files.PassportStampedPage);
      }
    });

    return formData;
  };

  const handleGenerateOTP = async () => {
    // Check if encryption is ready before proceeding
    if (!encryptionReady) {
      setExtraUserDataError(t('EncryptionConfigError') || 'Encryption is not properly configured');
      return;
    }

    const isPassportNumber = !!extensionLimitDetails.PassportNumber;
    const referenceNumber = isPassportNumber
      ? extensionLimitDetails.PassportNumber
      : extensionLimitDetails.NationalId;
    setLoading(true);
    try {
      const userDetails = await GetExtraUserDetails(referenceNumber, isPassportNumber);
    
      if (userDetails) {
        const otpResponse = await GenerateOTP(userDetails.CustomerNo);
        var data = constructDocumentsFormData(userDetails.CustomerNo);
        setFilesData(data);
        if (otpResponse && otpResponse.output.success === '1') {
          setGenerateOTPResponse(otpResponse);
          setActiveStep(2);
          setLoading(false);
        } else {
          setLoading(false);
        }
      }
    } catch (error) {
      setLoading(false);
    //  console.error('Error generating OTP:', error);
      // Optionally set an error state or show a notification to the user
    }
  };

  const GetExtraUserDetails = async (referenceNumber, isPassportNumber) => {
    try {
      // Get the API host
      const apiHost = config.sitecoreApiHost || config.hostname || window.location.origin;
      
      // Ensure we have a valid CSRF token
      await refreshCSRFTokenIfNeeded(apiHost);
      const csrfToken = getCSRFToken();

      if (!csrfToken) {
        throw new Error('CSRF token not available. Please refresh the page and try again.');
      }

      const url = `${apiHost}${ExtraUserDetailsEndpoint}`;
      
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Important for cookies
        headers: {
          'Content-Type': 'application/json',
          '__RequestVerificationToken': csrfToken,
        },
        body: JSON.stringify({
          ReferenceNumber: referenceNumber,
          IsPassportNumber: isPassportNumber,
          Lang: i18n.language,
          IsMock: IsMockExtraUserDetailsEndpoint || false
        })
      });

      // Handle CSRF token validation failure
      if (response.status === 403) {
       // console.warn('CSRF validation failed. Clearing token...');
        clearCSRFToken();
        throw new Error('Security validation failed. Please refresh the page and try again.');
      }

      if (response.status === 401) {
        throw new Error('Authentication required. Please refresh the page and try again.');
      }

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status} ${response.statusText}`);
      }

      const encryptedResponse = await response.json();
      
      // Check if response contains encrypted data
      if (encryptedResponse.Data && encryptedResponse.Data.CipherText && encryptedResponse.Data.IV) {
      //  console.log('Encrypted response received, decrypting...');
        
        try {
          // Decrypt the data using key from config
          const decryptedData = await decryptData(encryptedResponse.Data);
         // console.log('Data decrypted successfully');
          
          setExtraUserData(decryptedData); // Set the user data to state
          return decryptedData; // Return data for further processing
        } catch (decryptError) {
        //  console.error('Decryption error:', decryptError);
          throw new Error('Failed to decrypt response data. Please try again or contact support.');
        }
      } else {
        // Handle non-encrypted error responses
       // console.log('Non-encrypted response:', encryptedResponse);
        
        if (encryptedResponse.Message) {
          throw new Error(encryptedResponse.Message);
        }
        
        // Fallback: if response has Data but no encryption, use it directly (backward compatibility)
        if (encryptedResponse.Data) {
          setExtraUserData(encryptedResponse); // Set the user data to state
          return encryptedResponse; // Return data for further processing
        }
        
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
    //  console.error('Error in GetExtraUserDetails:', error);
      
      // Set user-friendly error messages
      if (error.message.includes('CSRF') || error.message.includes('Security')) {
        setExtraUserDataError(t('SecurityErrorMsg') || 'Security validation failed. Please refresh the page.');
      } else if (error.message.includes('Authentication')) {
        setExtraUserDataError(t('AuthenticationErrorMsg') || 'Authentication required. Please refresh the page.');
      } else if (error.message.includes('decrypt') || error.message.includes('Encryption')) {
        setExtraUserDataError(t('DecryptionErrorMsg') || 'Failed to process secure data. Please try again.');
      } else if (error.message.includes('configured')) {
        setExtraUserDataError(t('ConfigErrorMsg') || 'Application is not properly configured. Please contact support.');
      } else {
        setExtraUserDataError(t('GenericErrorMsg') || error.message);
      }
      
      throw error; // Rethrow to handle in the calling function
    }
  };

  // const GetExtraUserDetails = async (referenceNumber, isPassportNumber) => {
  //   try {
  //     // const url = `https://fabheadlessjsssc.dev.local/api/extrauserdata/GetExtraUserDetails?ReferenceNumber=${referenceNumber}&IsPassportNumber=${isPassportNumber}&Lang=${i18n.language}&IsMock=true`;
  //     const url = `${config.hostname}${ExtraUserDetailsEndpoint}?ReferenceNumber=${referenceNumber}&IsPassportNumber=${isPassportNumber}&Lang=${i18n.language}&IsMock=${IsMockExtraUserDetailsEndpoint}`;
  //     const response = await fetch(url);

  //     if (!response.ok) {
  //       throw new Error('Failed to fetch extra user details');
  //     }

  //     const data = await response.json();
  //     setExtraUserData(data); // Set the user data to state
  //     return data; // Return data for further processing
  //   } catch (error) {
  //     setExtraUserDataError(error.message);
  //     throw error; // Rethrow to handle in the calling function
  //   }
  // };

  const GenerateOTP = async (customerNo) => {
    try {
      //const url = `https://fabheadlessjsssc.dev.local/api/ibanotp/generate?mock=1&reference=${customerNo}`;
      const url = `${config.hostname}${GenerateOTPEndpoint}?mock=${IsMockGenerateOTPEndpoint}&reference=${customerNo}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to generate OTP');
      }

      const data = await response.json();
      if (data?.output?.success === '1') {
        setGenerateOTPResponse(data); // Set the OTP response to state
        return data; // Return data for further processing
      } else {
        setGenerateOTPResponseError(t('otpGenerationFailed'));
        return null;
      }
    } catch (error) {
      setGenerateOTPResponseError(error.message);
      throw error; // Rethrow to handle in the calling function
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  }

  return (
    <div className="DocumentOneContainer">
      {/* Encryption Configuration Error Alert */}
      {!encryptionReady && (
        <div className="alert alert-danger" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '5px' }}>
          ⚠️ {t('EncryptionConfigError') || 'Encryption configuration error. Please contact support.'}
        </div>
      )}

      {/* header */}
      <div className="headerContainer">
        {filesizeErrorMessage && (
          <span className="error-message">{filesizeErrorMessage}</span>
        )} 
        <span className="headerTitle">{t('NecessaryDocuments')}</span>
        <span className="headerSubtitle">
          {t('StepOneDescDocument')}
        </span>
      </div>

      {/* card one  */}
      {trips.map((trip, index) => (
        <div key={trip.TripID} className="CardContainer">
          {/* header */}
          <div className="headerContainer">
            <span className="title">
              {t('Trip')} {index + 1} - {trip.Country}
            </span>
            <span className="subTitle">{t('DocumentStepOneSubTitle')}</span>
          </div>

          {/* inbound details */}
          <div className="inboundConitainer">
            <div className='tripsDates'>
              <img src={dateIcon} alt="dateicon" className="dateICon" />
              <div className="dateContainer">
                <div className="dateDetails">
                  <span className="header">{t('Outbound')}:</span>
                  <span className="date">{new Date(trip.Outbound).toLocaleDateString()}</span>
                </div>

                <div className="dateDetails">
                  <span className="header"> {t('Inbound')}: </span>
                  <span className="date">{new Date(trip.Inbound).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* passport pages */}
          {['Passport1stPage', 'PassportStampedPage'].map((pageType) => (
            <div key={pageType} className="passportContainer">
              {/* header */}
              <div className="passHeader">
                <span className="passTitle">
                  {pageType === 'Passport1stPage' ? t('Passport1stPage') : t('PassportStampedPage')}
                </span>
                <span className="passSubTitle">
                  {pageType === 'Passport1stPage'
                    ? t('Passport1stPageDesc')
                    : t('PassportStampedPageDesc')}
                </span>
              </div>

              {/* attachment */}
              <div className="attachmentContainer">
                <input
                  type="file"
                  id={`${pageType}-${trip.TripID}`}
                  accept=".png,.jpg,.jpeg,.pdf"
                  className="fileInput"
                  onChange={(event) => handleFileChange(trip.TripID, pageType, event)}
                />
                <label htmlFor={`${pageType}-${trip.TripID}`} className="fileInputLabel">
                  <span>{fileNames[trip.TripID]?.[pageType] || t('AttachDocument')}</span>
                  <img src={paperClip} alt="attachment icon" className="paperclip" />
                </label>
                {fileNamesError[trip.TripID]?.[pageType] && (
                  <span className="errorText">{fileNamesError[trip.TripID][pageType]}</span>
                )}

                <label className="fileInputLabelAllowedTypes">
                  <ul>
                    <li style={{ marginBottom: '5px' }}>{t('AllowedTypes')}</li>
                    <li>{t('AllowedSize')}</li>
                  </ul>
                </label>
              </div>
            </div>
          ))}

          <div className="error-message">
            {generateOTPResponseError}
          </div>
          {extraUserDataError && (
            <div className="error-message">
              {extraUserDataError}
            </div>
          )}
        </div>
      ))}

      {/* buttons */}
      <div className="btnContainer">
        {/* <Button variant="outlined" onClick={() => setActiveStep(1)}>
          {t('Previous')}
        </Button> */}
        <Button 
          disabled={!isFormValid || !encryptionReady} 
          onClick={() => handleGenerateOTP()}
        >
          {t('Continue')}
        </Button>
      </div>
    </div>
  );
};

export default withTranslation()(withSitecoreContext()(DocumentStepOne));